import React, { useEffect, useState, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import { useSelector } from "react-redux";

// Create a dynamic keyframes function based on content length
const createScrollAnimation = (contentWidth) => keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(-${contentWidth}%); }
`;

const ScrollMessageContainer = styled.div`
  width: 100%;
  overflow: hidden;
  position: relative;
  background-color: rgb(240, 242, 245);
`;

const ScrollingMessage = styled.div`
  white-space: nowrap;
  display: inline-block;
  color: red;
  font-weight: 500;
  font-size: 1.5rem;
  padding-right: 50px; /* Add space between repetitions */

  ${({ contentWidth, duration }) => css`
    animation: ${createScrollAnimation(contentWidth)} ${duration}s linear
      infinite;
  `}
`;

const ScrollBar = () => {
  const information = useSelector(
    (state) => state?.globleSlice?.broadCastExpiry
  );
  const notification = useSelector(
    (state) => state?.globleSlice?.notification?.marque
  );
  const [duration, setDuration] = useState(20);
  const textRef = useRef(null);

  const marqueMessage = notification?.reduce((acc, curr, index) => {
    acc += `${curr?.message} ${index === notification?.length - 1 ? "" : "|"} `;
    return acc;
  }, " ");

  const fullMessage = marqueMessage?.length > 0 ? marqueMessage : information;

  // Calculate appropriate animation duration based on content length and screen width
  useEffect(() => {
    const calculateDuration = () => {
      if (textRef.current) {
        const textWidth = textRef.current.offsetWidth;
        const screenWidth = window.innerWidth;

        // Base factor - higher values make the animation slower
        let baseFactor = 15;

        // Adjust factor based on screen size - larger screens get slower animation
        if (screenWidth > 1200) {
          baseFactor = 25; // Much slower for large desktop screens
        } else if (screenWidth > 768) {
          baseFactor = 20; // Slower for smaller desktop/laptop screens
        } else {
          baseFactor = 15; // Faster for mobile screens
        }

        // Calculate duration based on content length relative to screen size
        const calculatedDuration = (textWidth / screenWidth) * baseFactor;

        // Set minimum and maximum duration to prevent extremes
        setDuration(Math.min(Math.max(15, calculatedDuration), 40));
      }
    };

    calculateDuration();

    // Add resize listener to recalculate on screen size changes
    window.addEventListener("resize", calculateDuration);
    return () => window.removeEventListener("resize", calculateDuration);
  }, [fullMessage]);

  return (
    <ScrollMessageContainer>
      <ScrollingMessage ref={textRef} contentWidth={100} duration={duration}>
        {fullMessage}
      </ScrollingMessage>
    </ScrollMessageContainer>
  );
};

export default React.memo(ScrollBar);
